import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminConfirmDialog } from '../../components/primitives/admin-confirm-dialog.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminApiError } from '../../api/admin-client.js';
import { useAdminCategories, useAdminCategoryMutations } from '../hooks/use-categories.js';
import { useAdminMediaLibrary } from '../../media/hooks/use-media.js';
import { CategoryForm } from '../components/category-form.js';
import type { AdminCategory } from '../types/catalog.js';

export function CategoriesPage() {
  const { data, isLoading, isError, refetch } = useAdminCategories();
  const { create, update, activate, deactivate } = useAdminCategoryMutations();
  // Used only to resolve a thumbnail URL for each category's linked image --
  // categories are a small, bounded taxonomy list (see the client-side
  // filtering note below), so a single bounded media-library fetch is
  // sufficient without a dedicated "fetch media by ids" backend endpoint.
  const { data: mediaLibrary } = useAdminMediaLibrary({ kind: 'image', limit: 100 });
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AdminCategory | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<AdminCategory | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const categories = data ?? [];
  const nameById = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);
  const mediaUrlById = useMemo(
    () => new Map((mediaLibrary ?? []).map((asset) => [asset.id, asset.url])),
    [mediaLibrary],
  );

  // Filters an already-bounded page (categories is reference/taxonomy
  // data, listed up to 200 rows at once -- not the full-dataset client
  // filtering Part 17 warns against for large, paginated resources like
  // products).
  const filtered = categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  const columns: AdminDataTableColumn<AdminCategory>[] = [
    {
      key: 'image',
      header: 'Image',
      render: (row) => {
        const url = row.imageMediaAssetId ? mediaUrlById.get(row.imageMediaAssetId) : null;
        return url ? (
          <img src={url} alt="" className="h-10 w-10 rounded-lg object-cover" />
        ) : (
          <div className="h-10 w-10 rounded-lg bg-gray-100" aria-hidden="true" />
        );
      },
    },
    { key: 'name', header: 'Name', render: (row) => row.name },
    {
      key: 'parentId',
      header: 'Parent',
      render: (row) => (row.parentId ? (nameById.get(row.parentId) ?? '—') : '—'),
    },
    { key: 'sortOrder', header: 'Order', render: (row) => row.sortOrder },
    { key: 'slug', header: 'Slug', render: (row) => row.slug },
    {
      key: 'isFeatured',
      header: 'Featured',
      render: (row) =>
        row.isFeatured ? <AdminStatusBadge label="Featured" tone="success" /> : <span className="text-gray-300">—</span>,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => (
        <AdminStatusBadge label={row.isActive ? 'Active' : 'Inactive'} tone={row.isActive ? 'success' : 'neutral'} />
      ),
    },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Categories"
        description="Manage the catalog's category hierarchy."
        breadcrumbs={[{ label: 'Categories' }]}
        actions={
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="bg-brand text-brand-foreground inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New category
          </button>
        }
      />

      {actionError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      <AdminSectionCard title="All categories" className="p-4 sm:p-5">
        {showCreate && (
          <div className="mb-4">
            <CategoryForm
              categories={categories}
              isSubmitting={create.isPending}
              onCancel={() => setShowCreate(false)}
              onSubmit={(input) => create.mutate(input, { onSuccess: () => setShowCreate(false) })}
            />
          </div>
        )}
        {editing && (
          <div className="mb-4">
            <CategoryForm
              categories={categories}
              initial={editing}
              isSubmitting={update.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={(input) =>
                update.mutate({ id: editing.id, input }, { onSuccess: () => setEditing(null) })
              }
            />
          </div>
        )}

        <AdminFilterBar>
          <AdminSearchInput value={search} onChange={setSearch} placeholder="Search categories…" label="Search categories" />
        </AdminFilterBar>

        {isError ? (
          <AdminErrorState message="Categories could not be loaded." onRetry={() => void refetch()} />
        ) : (
          <AdminDataTable
            columns={columns}
            rows={filtered}
            getRowId={(row) => row.id}
            isLoading={isLoading}
            isError={false}
            onRetry={() => void refetch()}
            emptyTitle="No categories yet"
            rowActions={(row) => (
              <div className="flex justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Edit
                </button>
                {row.isActive ? (
                  <button
                    type="button"
                    onClick={() => setPendingDeactivate(row)}
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => activate.mutate(row.id)}
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                  >
                    Activate
                  </button>
                )}
              </div>
            )}
          />
        )}
      </AdminSectionCard>

      <AdminConfirmDialog
        open={pendingDeactivate !== null}
        title={`Deactivate "${pendingDeactivate?.name}"?`}
        description="Deactivated categories are hidden from customers but keep all their products and history."
        confirmLabel="Deactivate"
        isConfirming={deactivate.isPending}
        onCancel={() => setPendingDeactivate(null)}
        onConfirm={() => {
          if (!pendingDeactivate) return;
          setActionError(null);
          deactivate.mutate(pendingDeactivate.id, {
            onSuccess: () => setPendingDeactivate(null),
            onError: (error) =>
              setActionError(error instanceof AdminApiError ? error.message : 'Could not deactivate.'),
          });
        }}
      />
    </AdminContent>
  );
}
